import { createFileRoute, useLoaderData } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { useCallback } from 'react';

import { defaultNS, useTranslation } from '@/lib/i18n';

import { AlertPage } from '../-shared/AlertPage';
import { BulkDeactivationButton } from '../-shared/BulkDeactivationButton';
import { createSearchSchema } from '../-shared/search-schema';
import type {
  AlertRowWithUserAndWorker,
  AlertSearchParams,
} from '../-shared/types';
import {
  createAlertExportHook,
  createAlertQueryHook,
} from '../-shared/useAlertQuery';
import { SoftwareAlertType } from '../../-zod-schema';

import {
  ColumnKey,
  SortableColumnKey,
  useSoftwareAlertsAlumniColumns,
} from './-hooks/useColumns';

const SearchSchema = createSearchSchema(SortableColumnKey);

const useAlertQuery = createAlertQueryHook<AlertRowWithUserAndWorker>({
  category: 'ALUMNI',
  queryKeySegment: 'alumni',
  sortColumnMap: {
    [ColumnKey.appUser]: 'USER',
    [ColumnKey.app]: 'APP',
    [ColumnKey.worker]: 'PERSON',
    user_actualRoleName: 'APP_USER_ROLE',
    user_statusName: 'APP_USER_STATUS',
    user_lastSignedInAt: 'LAST_SIGNED_IN_AT',
    user_createdAt: 'CREATED_AT',
  },
  filterAndMap: (items): AlertRowWithUserAndWorker[] =>
    items
      .filter((item) => item.user && item.worker)
      .map((item) => ({
        ...item,
        user: item.user as NonNullable<typeof item.user>,
        worker: item.worker as NonNullable<typeof item.worker>,
      })),
});

const useExport = createAlertExportHook('ALUMNI');

export const Route = createFileRoute(
  '/_staff/software/_layout/alerts/_alerts/alumni/',
)({
  validateSearch: zodValidator(SearchSchema),
  component: SoftwareAlertsAlumni,
});

function SoftwareAlertsAlumni() {
  const navigate = Route.useNavigate();
  const search = Route.useSearch();
  const counts = useLoaderData({ from: Route.parentRoute.id });
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'pages.software.alert',
  });

  const { data, isPending } = useAlertQuery(search);
  const { mutate: onExport, isPending: exporting } = useExport(search);
  const columns = useSoftwareAlertsAlumniColumns();

  const onSearch = useCallback(
    (query: Partial<AlertSearchParams>) => {
      navigate({ search: (prev) => ({ ...prev, ...query }) as typeof prev });
    },
    [navigate],
  );

  return (
    <AlertPage
      alertTypeKey={SoftwareAlertType.alumni.key}
      helpTitle={t('help.alumni.title')}
      helpText={t('help.alumni.text')}
      counts={counts}
      search={search}
      onSearch={onSearch}
      data={data}
      isPending={isPending}
      exporting={exporting}
      onExport={onExport}
      columns={columns}
      extraFilterActions={<BulkDeactivationButton selectParam="alumni" />}
    />
  );
}
